/**
 * AmbientDisplay — Full-screen ambient operations display for employee rooms.
 *
 * DRR-183 Phase 1+2+3: Full-screen display with two modes:
 *   - rotation: Auto-cycles through renderers (Phase 2)
 *   - dashboard: Split-screen simultaneous panels (Phase 3)
 *
 * Renders without AppShell (no sidebar, no header, no navigation).
 * Staff login required. Inactivity auto-lock enforced.
 *
 * Building blocks:
 *   - DRR-178: SpatialRenderer, KanbanRenderer
 *   - DRR-135: Traversal lens toggle (staff vs resident view)
 *   - Real-time: useTaskSubscriptions + useAlertSubscriptions
 *   - DRR-183: useInactivityLock (auto-lock after timeout)
 */

import { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Group,
  Text,
  Badge,
  Overlay,
  Center,
  Button,
  Paper,
  Title,
  SegmentedControl,
} from '@mantine/core';
import {
  IconLock,
  IconWifi,
  IconWifiOff,
  IconAlertTriangle,
  IconRefresh,
  IconLayoutDashboard,
} from '@tabler/icons-react';
import { useInactivityLock } from '../../hooks/useInactivityLock';
import { AmbientLayoutWrapper, type AmbientRendererType } from './AmbientLayoutWrapper';
import { AmbientDashboard, type DashboardPanelConfig } from './AmbientDashboard';
import type { AlertTickerItem } from './AlertTicker';
import type { SpatialCoordinate } from '../renderers/SpatialRenderer';
import type { PrimitiveItem } from '../../hooks/usePrimitiveData';

export type AmbientDisplayMode = 'rotation' | 'dashboard';

/** Ambient display configuration from st-0-2 Setting config */
export interface AmbientConfig {
  defaultRenderer: 'spatial' | 'kanban';
  defaultDimension: string;
  inactivityTimeoutMinutes: number;
  alertSeverityThreshold: string;
  refreshIntervalSeconds: number;
  fontScale: number;
  criticalOverlayDismissSeconds: number;
  /** Phase 2: Rotation configuration */
  rotationRenderers: AmbientRendererType[];
  rotationIntervalSeconds: number;
  /** Phase 3: Display mode + dashboard panels */
  displayMode: AmbientDisplayMode;
  dashboardPanels: DashboardPanelConfig[];
}

const DEFAULT_CONFIG: AmbientConfig = {
  defaultRenderer: 'spatial',
  defaultDimension: 'location',
  inactivityTimeoutMinutes: 15,
  alertSeverityThreshold: 'HIGH',
  refreshIntervalSeconds: 30,
  fontScale: 1.5,
  criticalOverlayDismissSeconds: 30,
  rotationRenderers: ['spatial', 'kanban'],
  rotationIntervalSeconds: 30,
  displayMode: 'dashboard',
  dashboardPanels: [
    { slot: 'left', renderer: 'spatial', dimension: 'location' },
    { slot: 'right', renderer: 'kanban', dimension: 'status' },
  ],
};

interface AmbientDisplayProps {
  items: PrimitiveItem[];
  config?: Partial<AmbientConfig>;
  isConnected?: boolean;
  criticalAlert?: { message: string; timestamp: string } | null;
  onDismissAlert?: () => void;
  coordinates?: SpatialCoordinate[];
  floorPlanUrl?: string;
  /** Phase 3: Alert history for ticker */
  alertHistory?: AlertTickerItem[];
}

export function AmbientDisplay({
  items,
  config: configOverride,
  isConnected = true,
  criticalAlert,
  onDismissAlert,
  coordinates,
  floorPlanUrl,
  alertHistory = [],
}: AmbientDisplayProps) {
  const config = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...configOverride }),
    [configOverride],
  );

  const { isLocked, unlock } = useInactivityLock(config.inactivityTimeoutMinutes);
  const [traversalLens, setTraversalLens] = useState<'staff' | 'resident'>('resident');
  const [displayMode, setDisplayMode] = useState<AmbientDisplayMode>(config.displayMode);

  const ambientStyle = {
    '--ambient-font-scale': config.fontScale,
    fontSize: `clamp(1.2rem, ${config.fontScale}vw, 2rem)`,
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    background: 'var(--mantine-color-body)',
  } as React.CSSProperties;

  return (
    <Box style={ambientStyle}>
      {/* Top bar: title + controls + connection status */}
      <Group
        justify="space-between"
        px="md"
        py={8}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'var(--mantine-color-body)',
          borderBottom: '1px solid var(--mantine-color-dark-4)',
        }}
      >
        <Text size="sm" c="dimmed" fw={500}>
          FERP Ambient Display
        </Text>

        <Group gap="xs">
          {/* Display mode toggle */}
          <SegmentedControl
            value={displayMode}
            onChange={(v) => setDisplayMode(v as AmbientDisplayMode)}
            data={[
              {
                value: 'rotation',
                label: (
                  <Group gap={4}>
                    <IconRefresh size={14} />
                    <Text size="xs">Rotation</Text>
                  </Group>
                ),
              },
              {
                value: 'dashboard',
                label: (
                  <Group gap={4}>
                    <IconLayoutDashboard size={14} />
                    <Text size="xs">Dashboard</Text>
                  </Group>
                ),
              },
            ]}
            size="xs"
          />

          {/* Connection status */}
          <Badge
            color={isConnected ? 'green' : 'red'}
            variant="filled"
            size="lg"
            leftSection={
              isConnected ? <IconWifi size={14} /> : <IconWifiOff size={14} />
            }
          >
            {isConnected ? 'Live' : 'Disconnected'}
          </Badge>
        </Group>
      </Group>

      {/* Main content area */}
      <Box pt={44} style={{ height: '100%' }}>
        {displayMode === 'rotation' ? (
          <AmbientLayoutWrapper
            items={items}
            rotation={{
              renderers: config.rotationRenderers,
              intervalSeconds: config.rotationIntervalSeconds,
            }}
            pauseRotation={!!criticalAlert}
            traversalLens={traversalLens}
            onTraversalLensChange={setTraversalLens}
            coordinates={coordinates}
            floorPlanUrl={floorPlanUrl}
          />
        ) : (
          <AmbientDashboard
            items={items}
            alerts={alertHistory}
            panels={config.dashboardPanels}
            coordinates={coordinates}
            floorPlanUrl={floorPlanUrl}
          />
        )}
      </Box>

      {/* CRITICAL alert overlay */}
      {criticalAlert && (
        <Overlay fixed zIndex={200}>
          <Center h="100%">
            <Paper
              p="xl"
              radius="lg"
              shadow="xl"
              style={{
                background: 'var(--mantine-color-red-9)',
                maxWidth: '80vw',
                textAlign: 'center',
              }}
            >
              <Stack align="center" gap="md">
                <IconAlertTriangle size={64} color="white" />
                <Title order={1} c="white">
                  CRITICAL ALERT
                </Title>
                <Text size="xl" c="white" fw={500}>
                  {criticalAlert.message}
                </Text>
                <Text size="sm" c="red.2">
                  {criticalAlert.timestamp}
                </Text>
                <Button
                  color="white"
                  variant="outline"
                  size="lg"
                  onClick={onDismissAlert}
                >
                  Acknowledge
                </Button>
              </Stack>
            </Paper>
          </Center>
        </Overlay>
      )}

      {/* Inactivity lock overlay */}
      {isLocked && (
        <Overlay fixed zIndex={300}>
          <Center h="100%">
            <Paper
              p="xl"
              radius="lg"
              shadow="xl"
              style={{
                background: 'var(--mantine-color-dark-7)',
                maxWidth: '50vw',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={unlock}
            >
              <Stack align="center" gap="md">
                <IconLock size={48} color="var(--mantine-color-gray-5)" />
                <Title order={2} c="gray.3">
                  Display Locked
                </Title>
                <Text size="lg" c="dimmed">
                  Tap anywhere to unlock
                </Text>
              </Stack>
            </Paper>
          </Center>
        </Overlay>
      )}
    </Box>
  );
}
